<template>
  <div class="ratios">
    <div v-for="r in rows" :key="r.label" class="ratio-row">
      <div class="r-label">{{ r.label }}</div>
      <svg :viewBox="`0 0 ${W} 26`" class="r-chart">
        <line :x1="x(0)" :x2="x(max)" y1="13" y2="13" class="axis" />
        <line :x1="x(r.target)" :x2="x(r.target)" y1="4" y2="22" class="target" />
        <circle :cx="x(r.value2027)" cy="13" r="5" class="dot" />
      </svg>
      <div class="r-vals">
        <b>{{ r.value2027 }}</b> <span class="muted">vs target {{ r.target }}</span>
      </div>
    </div>
    <p class="dynamic">
      At the current levers — {{ addedClasses }} added class{{ addedClasses === 1 ? '' : 'es' }},
      enrolment {{ enrolmentPct >= 0 ? '+' : '' }}{{ enrolmentPct.toFixed(0) }}% —
      the school carries <b>{{ dynamicRatio.toFixed(1) }}</b> students per staff member
      ({{ dynStudents.toFixed(0) }} students, {{ dynStaff.toFixed(1) }} FTE).
    </p>
    <p class="note">
      A lower students-per-staff number means more staff per student — richer staffing, higher cost.
      Targets are the model's own benchmarks (KPIs tab). Added classes assume their capacity students
      and 2 staff (teacher + TA) each.
    </p>
  </div>
</template>

<script>
import { scaleLinear } from '../../utils/chartUtils';
import { CLASSROOM_TYPES } from '../../utils/boardEngine';

export default {
  name: 'RatiosPanel',
  props: {
    ratios: { type: Object, required: true }, // { rows, students2027, staffFte2027 }
    classrooms: { type: Array, default: () => [] },
    enrolmentPct: { type: Number, default: 0 },
  },
  data: () => ({ W: 380 }),
  computed: {
    rows() { return this.ratios.rows; },
    max() {
      return Math.max(...this.rows.flatMap((r) => [r.target, r.value2027])) * 1.15;
    },
    x() { return scaleLinear([0, this.max], [4, this.W - 4]); },
    enabled() { return this.classrooms.filter((c) => c.enabled); },
    addedClasses() { return this.enabled.length; },
    dynStudents() {
      const extra = this.enabled.reduce((s, c) => s + (CLASSROOM_TYPES[c.type]?.students || 0), 0);
      return this.ratios.students2027 * (1 + this.enrolmentPct / 100) + extra;
    },
    dynStaff() { return this.ratios.staffFte2027 + this.enabled.length * 2; },
    dynamicRatio() { return this.dynStudents / this.dynStaff; },
  },
};
</script>

<style scoped>
.ratio-row {
  display: grid; grid-template-columns: minmax(150px, 1.2fr) 2fr minmax(110px, 0.9fr);
  gap: 12px; align-items: center; margin-bottom: 8px;
}
.r-label { font-size: 12px; color: var(--ink); }
.r-chart { width: 100%; height: 26px; display: block; }
.axis { stroke: var(--hairline); stroke-width: 1; }
.target { stroke: var(--ink); stroke-width: 2; }
.dot { fill: var(--red); stroke: var(--paper); stroke-width: 2; }
.r-vals { font-size: 12px; text-align: right; font-variant-numeric: tabular-nums; }
.muted { color: var(--muted); }
.dynamic { font-size: 12.5px; color: var(--ink); margin: 14px 0 0; line-height: 1.5; }
.note { font-size: 10.5px; color: var(--muted); line-height: 1.55; margin: 8px 0 0; }
</style>
